import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Edit2, ChevronRight, X } from 'lucide-react';

const FORMES_JURIDIQUES = ['SARL', 'SAS', 'SA', 'SNC', 'EI', 'EIRL', 'SCI', 'Association', 'Autre'];
const SECTEURS_CPS = [
  { code: '1', label: 'Ecoles / Associations' }, { code: '2', label: 'Agriculture / Aquaculture' },
  { code: '3', label: 'Acconage' }, { code: '5', label: 'Professions liberales' },
  { code: '6', label: 'Commerce / Services' }, { code: '7', label: 'BTP / Transports / Industrie' },
  { code: '8', label: 'Services publics' }, { code: '11', label: 'Gens de maison' }, { code: 'PH', label: 'Peche hauturiere' }
];
const MODES = [
  { value: 'autonome', label: 'Autonome', desc: 'Le client gère tout' },
  { value: 'semi_delegation', label: 'Semi-délégation', desc: 'Client saisit, Moana Paye valide' },
  { value: 'delegation', label: 'Délégation', desc: 'Moana Paye gère tout' }
];

const Field = ({ label, children, required }) => (
  <div>
    <label className="text-slate-400 text-xs font-medium mb-1 block">{label}{required && <span className="text-[#E8A020] ml-0.5">*</span>}</label>
    {children}
  </div>
);
const Input = (props) => <input {...props} className={`w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A] ${props.className || ''}`} />;
const Select = ({ children, ...props }) => <select {...props} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#2D5A5A]">{children}</select>;

export default function EntreprisesPage() {
  const { mpFetch } = useMoanaPaye();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => mpFetch('/api/moana-paye/entreprises').then(setList).catch(console.error);
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ raison_sociale: '', forme_juridique: 'SARL', ville: 'Papeete', ile: 'Tahiti', code_secteur_cps: '6', taux_at_mp: 0.77, periodicite_paie: 'mensuelle', mode_utilisation: 'autonome', couleur_principale: '#2D5A5A', couleur_secondaire: '#E8A020' });
    setEditId(null);
    setShowForm(true);
  };

  const openEdit = async (id) => {
    const data = await mpFetch(`/api/moana-paye/entreprises/${id}`);
    setForm(data);
    setEditId(id);
    setShowForm(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editId) {
        await mpFetch(`/api/moana-paye/entreprises/${editId}`, { method: 'PUT', body: JSON.stringify(form) });
      } else {
        await mpFetch('/api/moana-paye/entreprises', { method: 'POST', body: JSON.stringify(form) });
      }
      setShowForm(false);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  if (showForm) return (
    <div data-testid="mp-entreprise-form">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">{editId ? 'Modifier' : 'Nouvelle'} entreprise</h2>
        <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
      </div>
      <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 space-y-6">
        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase">Identification</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Raison sociale" required><Input value={form.raison_sociale || ''} onChange={e => set('raison_sociale', e.target.value)} data-testid="mp-ent-raison" /></Field>
          <Field label="N Tahiti"><Input value={form.numero_tahiti || ''} onChange={e => set('numero_tahiti', e.target.value)} /></Field>
          <Field label="N RCS"><Input value={form.numero_rcs || ''} onChange={e => set('numero_rcs', e.target.value)} /></Field>
          <Field label="Forme juridique"><Select value={form.forme_juridique || ''} onChange={e => set('forme_juridique', e.target.value)}>{FORMES_JURIDIQUES.map(f => <option key={f}>{f}</option>)}</Select></Field>
          <Field label="Téléphone"><Input value={form.telephone || ''} onChange={e => set('telephone', e.target.value)} /></Field>
          <Field label="Email"><Input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
        </div>

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Adresse</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Adresse"><Input value={form.adresse_ligne1 || ''} onChange={e => set('adresse_ligne1', e.target.value)} /></Field>
          <Field label="Complement"><Input value={form.adresse_ligne2 || ''} onChange={e => set('adresse_ligne2', e.target.value)} /></Field>
          <Field label="Code postal"><Input value={form.code_postal || ''} onChange={e => set('code_postal', e.target.value)} /></Field>
          <Field label="Ville"><Input value={form.ville || ''} onChange={e => set('ville', e.target.value)} /></Field>
          <Field label="Ile"><Input value={form.ile || ''} onChange={e => set('ile', e.target.value)} /></Field>
        </div>

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Social & Paie</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="CCT applicable"><Input value={form.cct_applicable || ''} onChange={e => set('cct_applicable', e.target.value)} /></Field>
          <Field label="Secteur activite"><Input value={form.secteur_activite || ''} onChange={e => set('secteur_activite', e.target.value)} /></Field>
          <Field label="Secteur CPS"><Select value={form.code_secteur_cps || '6'} onChange={e => set('code_secteur_cps', e.target.value)}>{SECTEURS_CPS.map(s => <option key={s.code} value={s.code}>{s.code} - {s.label}</option>)}</Select></Field>
          <Field label="Taux AT/MP (%)"><Input type="number" step="0.01" value={form.taux_at_mp ?? 0.77} onChange={e => set('taux_at_mp', parseFloat(e.target.value))} /></Field>
          <Field label="Periodicite paie"><Select value={form.periodicite_paie || 'mensuelle'} onChange={e => set('periodicite_paie', e.target.value)}><option value="mensuelle">Mensuelle</option><option value="bimensuelle">Bimensuelle</option></Select></Field>
          <Field label="Mode utilisation">
            <Select value={form.mode_utilisation || 'autonome'} onChange={e => set('mode_utilisation', e.target.value)}>
              {MODES.map(m => <option key={m.value} value={m.value}>{m.label} - {m.desc}</option>)}
            </Select>
          </Field>
          <Field label="Décompte congés">
            <Select value={form.decompte_conges || 'ouvres'} onChange={e => set('decompte_conges', e.target.value)}>
              <option value="ouvres">Jours ouvres (lun-ven)</option>
              <option value="ouvrables">Jours ouvrables (lun-sam)</option>
            </Select>
          </Field>
        </div>

        <h3 className="text-[#E8A020] text-xs font-semibold tracking-wider uppercase pt-2">Personnalisation bulletin</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Couleur principale">
            <div className="flex items-center gap-2">
              <input type="color" value={form.couleur_principale || '#2D5A5A'} onChange={e => set('couleur_principale', e.target.value)} className="w-10 h-10 rounded border-0 cursor-pointer" />
              <Input value={form.couleur_principale || '#2D5A5A'} onChange={e => set('couleur_principale', e.target.value)} />
            </div>
          </Field>
          <Field label="Couleur secondaire">
            <div className="flex items-center gap-2">
              <input type="color" value={form.couleur_secondaire || '#E8A020'} onChange={e => set('couleur_secondaire', e.target.value)} className="w-10 h-10 rounded border-0 cursor-pointer" />
              <Input value={form.couleur_secondaire || '#E8A020'} onChange={e => set('couleur_secondaire', e.target.value)} />
            </div>
          </Field>
          <Field label="Apercu">
            <div className="h-10 rounded-lg flex items-center px-3 text-white text-sm font-medium" style={{ backgroundColor: form.couleur_principale || '#2D5A5A' }}>
              <span style={{ color: form.couleur_secondaire || '#E8A020' }}>Bulletin de paie</span>
            </div>
          </Field>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
          <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Annuler</button>
          <button onClick={save} disabled={saving || !form.raison_sociale} className="px-5 py-2 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50" data-testid="mp-ent-save">
            {saving ? 'Enregistrement...' : editId ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="mp-entreprises">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Entreprises</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-[#2D5A5A] hover:bg-[#3a7272] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors" data-testid="mp-ent-new">
          <Plus className="w-4 h-4" /> Nouvelle entreprise
        </button>
      </div>
      {list.length === 0 ? (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl p-10 text-center">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Aucune entreprise</p>
          <button onClick={openNew} className="mt-3 text-[#E8A020] text-sm hover:underline">Creer la premiere entreprise</button>
        </div>
      ) : (
        <div className="space-y-2">
          {list.map(ent => (
            <div key={ent.id} className="bg-[#1a2332] border border-white/10 rounded-xl p-4 flex items-center justify-between hover:border-[#2D5A5A]/50 transition-colors cursor-pointer" onClick={() => openEdit(ent.id)} data-testid={`mp-ent-${ent.id}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#2D5A5A]/20 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#2D5A5A]" /></div>
                <div>
                  <div className="text-white font-medium text-sm">{ent.raison_sociale}</div>
                  <div className="text-slate-500 text-xs">{ent.ville} &middot; {ent.forme_juridique} &middot; {ent.nb_salaries} salarie(s)</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  ent.mode_utilisation === 'delegation' ? 'bg-[#E8A020]/15 text-[#E8A020]' :
                  ent.mode_utilisation === 'semi_delegation' ? 'bg-blue-500/15 text-blue-400' :
                  'bg-white/10 text-slate-400'
                }`}>{ent.mode_utilisation?.replace('_', '-')}</span>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
